import { useApi } from '@/api';
import { Asset, Page, PageSkeleton } from '@/components';
import { ACTIVITY_EVENT, DEFAULT_NETWORK } from '@/constants';
import { useSearchParams } from '@/hooks';
import {
  DropdownFilter,
  Flex,
  formatDatetime,
  Heading,
  useTableProps
} from '@blockanalitica/ui';
import { useParams } from 'react-router';
import EventsTable from './EventsTable';
import HistoricTabs from './HistoricTabs';

type Market = {
  underlying_symbol: string;
  underlying_name: string;
  underlying_address: string;
  total_supply: string;
  total_supply_usd: string;
  total_supply_change: string | null;
  total_supply_usd_change: string | null;
  total_supply_change_percentage: string | null;
  total_supply_usd_change_percentage: string | null;
  total_borrow: string;
  total_borrow_usd: string;
  total_borrow_change: string | null;
  total_borrow_usd_change: string | null;
  total_borrow_change_percentage: string | null;
  total_borrow_usd_change_percentage: string | null;
  supply_apy: string;
  supply_apy_1d: string | null;
  supply_apy_7d: string | null;
  supply_apy_30d: string | null;
  supply_apy_90d: string | null;
  supply_apy_365d: string | null;
  supply_apy_change: string | null;
  supply_apy_change_percentage: string | null;
  borrow_apy: string;
  borrow_apy_1d: string | null;
  borrow_apy_7d: string | null;
  borrow_apy_30d: string | null;
  borrow_apy_90d: string | null;
  borrow_apy_365d: string | null;
  borrow_apy_1d_change: string | null;
  borrow_apy_7d_change: string | null;
  borrow_apy_30d_change: string | null;
  borrow_apy_90d_change: string | null;
  borrow_apy_365d_change: string | null;
  supply_apy_1d_change: string | null;
  supply_apy_7d_change: string | null;
  supply_apy_30d_change: string | null;
  supply_apy_90d_change: string | null;
  supply_apy_365d_change: string | null;
  borrow_apy_change: string | null;
  borrow_apy_change_percentage: string | null;
  utilization_rate: string;
  utilization_rate_usd: string;
  utilization_rate_change: string | null;
  utilization_rate_change_percentage: string | null;
  tags: (string | null)[];
};

export default function MarketActivityPage() {
  const [date] = useSearchParams('date', '');
  const { marketAddress } = useParams() as {
    marketAddress: string;
  };
  const network = DEFAULT_NETWORK;

  const { pagination, sorting, filters } = useTableProps({
    pageSize: 10,
    initialSorting: '-order_index',
    filtersDebounceTime: 500,
    initialFilters: {
      selectedOption: ''
    }
  });

  const [data, error] = useApi<Market>({
    url: `/markets/${marketAddress}/`
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <PageSkeleton />;
  }

  const fromDate = date ? ` on ${formatDatetime(date)}` : '';

  return (
    <Page
      title={
        <Asset
          symbol={data.underlying_symbol}
          address={marketAddress}
          network={network}
          suffix={`Activity${fromDate}`}
          iconSize="4"
          fontSize="5"
        />
      }
      actions={
        date ? (
          <DropdownFilter
            options={ACTIVITY_EVENT}
            {...filters}
            withCryptoIcon={false}
          />
        ) : null
      }>
      <Flex gap={['m', 's']} flexDirection="column">
        {date ? null : (
          <>
            <HistoricTabs />
            <Flex justifyContent="space-between" alignItems="center">
              <Heading size="2">Events</Heading>
              <DropdownFilter
                options={ACTIVITY_EVENT}
                {...filters}
                withCryptoIcon={false}
              />
            </Flex>
          </>
        )}

        <EventsTable
          pagination={pagination}
          sorting={sorting}
          eventType={filters.filtersApplied['selectedOption']}
          marketAddress={marketAddress}
          date={date}
        />
      </Flex>
    </Page>
  );
}
